const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

const STATES = {
  open: "open",
  closed: "closed",
};

class ConditionalDetails extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.summary = this.querySelector("summary");
    this.details = this.querySelector("details");
    this.content = this.querySelector("[data-content]");

    this.summary.addEventListener("click", (e) => {
      if (e.target.closest("a")) return;

      e.preventDefault();

      if (this._allowToggle) {
        this.details.open ? this.close() : this.open();
      }
    });

    this.lastAllowToggle = null;

    const toggleOpen = () => {
      if (!this._allowToggle) {
        this._forceOpen();
        this.summary.tabIndex = -1;
      } else if (this.lastAllowToggle === false && this._allowToggle) {
        this._forceClosed();
        this.summary.removeAttribute("tabindex");
      }

      this.lastAllowToggle = this._allowToggle;
    };

    toggleOpen();

    if (this.hasAttribute("data-swipe-to-close")) {
      this._initSwipeToClose();
    }

    this.onResize = debounce(toggleOpen, 200);

    window.addEventListener("resize", this.onResize);
    this.addEventListener("conditional-details:force-closed", () =>
      this._forceClosed(),
    );

    this.addEventListener("conditional-details:close", () => {
      this.close();
    });

    if (this._closeClickOutside) {
      this.handleDocumentClick = (e) => {
        if (this.details.open && !this.contains(e.target)) {
          this.close();
        }
      };

      this.handleKeyUp = (e) => {
        if (this.details.open && e.code.toUpperCase() === "ESCAPE") {
          this.close();
        }
      };

      document.addEventListener("keyup", this.handleKeyUp);
      document.addEventListener("click", this.handleDocumentClick);
    }

    if (this._accordionGroup) {
      this.handleAccordionGroup = (e) => {
        if (e.target === this) return;
        if (e.detail.group !== this._accordionGroup) return;

        this.close();
      };

      document.addEventListener(
        "conditional-details:close-others-in-group",
        this.handleAccordionGroup,
      );
    }

    if (this._saveStateKey) {
      try {
        const savedState = window.sessionStorage.getItem(this._saveStateKey);

        if (savedState) {
          const currentState = this.details.open ? STATES.open : STATES.closed;
          if (this._allowToggle && savedState !== currentState) {
            savedState === STATES.open
              ? this._forceOpen()
              : this._forceClosed();
          }
        }
      } catch (error) {
        console.log(`error applying saved accordion state`, error);
        // do nothing
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener(
      "conditional-details:close-others-in-group",
      this.handleAccordionGroup,
    );
  }

  _forceOpen() {
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.add("open");
    this.details.open = true;

    if (this._lockScroll) {
      window.closeFullDimmer(this);
      window.unlockScroll(this);
    } 

    if (this.content) {
      this._saveState(STATES.open);
      this.content.style.height = null;
      this.content.style.opacity = null;
      this.content.style.overflow = "visible";
    }
  }

  _forceClosed() {
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.remove("open");
    this.details.open = false;
    window.closeFullDimmer(this);
    window.unlockScroll(this);

    if (this.content) {
      this._saveState(STATES.closed);
      this.content.style.height = null;
      this.content.style.opacity = null;
      this.content.style.overflow = null;
    }
  }

  open() {
    if (this.details.open || !this._allowToggle) return;

    if (this._lockScroll) {
      window.openFullDimmer(this);
      window.lockScroll(this);
    }

    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.open = true;
    this.details.classList.add("open");

    if (this._accordionGroup) {
      this.dispatchEvent(
        new CustomEvent("conditional-details:close-others-in-group", {
          bubbles: true,
          detail: { group: this._accordionGroup },
        }),
      );
    }

    if (this.content) {
      this.content.style.height = 0;
      this.content.style.opacity = 0;

      this.handleTransitionEnd = (e) => {
        if (e.target !== this.content) return;
        this.removeEventListener("transitionend", this.handleTransitionEnd);
        this.content.style.height = null;
        this.content.style.overflow = "visible";
        this._saveState(STATES.open);
      };

      this.addEventListener("transitionend", this.handleTransitionEnd);

      window.requestAnimationFrame(() => {
        this.content.style.height = `${this.content.scrollHeight}px`;
        this.content.style.opacity = 1;
      });
    }
  }

  close() {
    if (!this.details.open || !this._allowToggle) return;

    window.closeFullDimmer(this);
    window.unlockScroll(this);

    if (this.content) {
      this.removeEventListener("transitionend", this.handleTransitionEnd);
      this.content.style.height = `${this.content.scrollHeight}px`;

      window.requestAnimationFrame(() => {
        this.handleTransitionEnd = (e) => {
          if (e.target !== this.content) return;

          this.removeEventListener("transitionend", this.handleTransitionEnd);
          this.details.open = false;
          this.content.style.height = null;
          this._saveState(STATES.closed);

          if (this._closeNestedOnClose) {
            this.querySelectorAll("conditional-details").forEach((el) => {
              el.dispatchEvent(
                new CustomEvent("conditional-details:force-closed"),
              );
            });
          }
        };

        this.addEventListener("transitionend", this.handleTransitionEnd);

        this.details.classList.remove("open");
        this.content.style.height = 0;
        this.content.style.opacity = 0;
        this.content.style.overflow = null;
      });
    } else {
      this.details.open = false;
    }
  }

  get _closeNestedOnClose() {
    return this.hasAttribute("close-nested-on-close");
  }

  get _closeClickOutside() {
    return this.hasAttribute("close-click-outside");
  }

  get _allowToggle() {
    return window.matchMedia(this._mediaQuery).matches;
  }

  get _lockScroll() {
    const mediaQuery = this.getAttribute("lock-scroll-when");

    if (!mediaQuery) return false;

    return window.matchMedia(mediaQuery).matches;
  }

  get _mediaQuery() {
    return this.getAttribute("allow-toggle-when") || "all";
  }

  get _accordionGroup() {
    return this.getAttribute("accordion-group");
  }

  get _saveStateKey() {
    const key = this.getAttribute("save-state");

    if (!key) return null;

    return `accordion-state-${key}`;
  }

  _saveState(state) {
    const key = this._saveStateKey;

    if (!key) return;

    try {
      window.sessionStorage.setItem(key, state);
    } catch (error) {
      console.log(`error saving accordion state`, error);
    }
  }

  _initSwipeToClose() {
    let startY = 0;
    let endY = 0;
    const threshold = 50; // minimum swipe distance in px

    this.details.addEventListener("touchstart", (e) => {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
    });

    this.details.addEventListener("touchmove", (e) => {
      if (e.touches.length !== 1) return;
      endY = e.touches[0].clientY;
    });

    this.details.addEventListener("touchend", () => {
      if (!this.details.open) return;

      const deltaY = endY - startY;
      if (deltaY > threshold) {
        this.close();
      }

      // reset
      startY = 0;
      endY = 0;
    });
  }
}

if (!customElements.get("conditional-details")) {
  customElements.define("conditional-details", ConditionalDetails);
}
